import { useEffect, useState } from "react";
import { Download, Upload, FlaskConical, CheckCircle2, Trash2, RefreshCw, AlertTriangle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Loader } from "@/components/portal/ui";
import { StatusMarker } from "@/components/portal/StatusMarker";

const CSV_HINT = "business_name,category,location,contact_email,contact_phone,website,description";

// Minimal CSV parser (auto delimiter , or ;) with quoted-field support.
function parseCsv(text) {
  const rows = [];
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim().length);
  if (!lines.length) return { header: [], rows: [] };
  const delim = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ";" : ",";
  const split = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (ch === delim && !q) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };
  const header = split(lines[0]).map((h) => h.toLowerCase());
  for (let i = 1; i < lines.length; i++) {
    const cells = split(lines[i]);
    const obj = {};
    header.forEach((h, idx) => { obj[h] = cells[idx] || ""; });
    rows.push({
      business_name: obj.business_name || obj.name || "",
      category: obj.category || obj.categories || "",
      location: obj.location || obj.city || "",
      contact_email: obj.contact_email || obj.email || "",
      contact_phone: obj.contact_phone || obj.phone || "",
      website: obj.website || "",
      description: obj.description || "",
    });
  }
  return { header, rows };
}

const ORIGIN_MARK = { real: "connected", seed: "synthetic", demo: "demo" };

export function MigrationPanel({ portalKey }) {
  const { t, L } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState("pending");
  const [batches, setBatches] = useState([]);

  const loadBatches = () => {
    api.get(`/control/portal/${portalKey}/import-batches`).then(({ data }) => setBatches(data.batches || []));
  };
  const load = () => {
    setData(null);
    api.get(`/control/portal/${portalKey}/data-origin`).then(({ data }) => setData(data));
    loadBatches();
  };
  useEffect(load, [portalKey]);

  const backup = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/control/portal/${portalKey}/backup`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `backup_${portalKey}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success(L({ de: "Backup heruntergeladen", fr: "Sauvegarde téléchargée", it: "Backup scaricato", en: "Backup downloaded" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runImport = async (commit) => {
    const parsed = parseCsv(csv);
    if (!parsed.rows.length) { toast.error(L({ de: "Keine Zeilen erkannt", fr: "Aucune ligne", it: "Nessuna riga", en: "No rows detected" })); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/control/providers/import`,
        { portal_key: portalKey, rows: parsed.rows, commit, default_status: status });
      setPreview(res);
      if (commit) {
        toast.success(L({ de: `${res.created} Anbieter importiert`, fr: `${res.created} importés`, it: `${res.created} importati`, en: `${res.created} imported` }));
        setCsv(""); setPreview(null); load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setOrigin = async (id, patch) => {
    setBusy(true);
    try {
      await api.patch(`/control/providers/${id}/origin`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const purge = async () => {    if (!window.confirm(L({
      de: "Alle synthetischen Seed-/Demo-Anbieter dieses Portals archivieren? Bitte vorher ein Backup herunterladen.",
      fr: "Archiver tous les fournisseurs seed/démo de ce portail ? Téléchargez d'abord une sauvegarde.",
      it: "Archiviare tutti i fornitori seed/demo di questo portale? Scarica prima un backup.",
      en: "Archive all synthetic seed/demo providers of this portal? Download a backup first.",
    }))) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/control/portal/${portalKey}/purge-seed`, { confirm: true, mode: "archive" });
      toast.success(L({ de: `${res.affected} archiviert`, fr: `${res.affected} archivés`, it: `${res.affected} archiviati`, en: `${res.affected} archived` }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const rollbackBatch = async (b) => {
    if (!window.confirm(L({
      de: `Diesen Import (${b.count} Anbieter, ${b.created_at?.slice(0, 10)}) rückgängig machen? Es werden AUSSCHLIESSLICH die Anbieter dieses Imports archiviert — Seed/Demo und andere Daten bleiben unberührt.`,
      fr: `Annuler cet import (${b.count} fournisseurs) ? Seuls les fournisseurs de cet import seront archivés.`,
      it: `Annullare questo import (${b.count} fornitori)? Solo i fornitori di questo import verranno archiviati.`,
      en: `Roll back this import (${b.count} providers)? ONLY this import's providers are archived — seed/demo and other data stay untouched.`,
    }))) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/control/portal/${portalKey}/import-batches/${b.id}/rollback`, { confirm: true, mode: "archive" });
      toast.success(L({ de: `${res.affected} Anbieter aus Import archiviert`, fr: `${res.affected} archivés`, it: `${res.affected} archiviati`, en: `${res.affected} providers archived` }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <Loader />;
  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="portal-migration">
      {/* Origin summary */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>
              {L({ de: "Datenherkunft", fr: "Origine des données", it: "Origine dati", en: "Data origin" })}
            </div>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {L({ de: "Echte vs. synthetische (Seed/Demo) Anbieter — vor dem Live-Gang bereinigen.", fr: "Réels vs synthétiques.", it: "Reali vs sintetici.", en: "Real vs synthetic providers — clean up before go-live." })}
            </p>
          </div>
          <button data-testid="migration-refresh" onClick={load} disabled={busy}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs disabled:opacity-50" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
            <RefreshCw className="h-3.5 w-3.5" /> {L({ de: "Aktualisieren", fr: "Actualiser", it: "Aggiorna", en: "Refresh" })}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[["real", CheckCircle2], ["seed", FlaskConical], ["demo", FlaskConical]].map(([k, I]) => (
            <div key={k} className="rounded-lg p-3 text-center" data-testid={`origin-count-${k}`} style={{ background: "var(--surface-alt)" }}>
              <I className="h-4 w-4 mx-auto mb-1" style={{ color: "var(--accent)" }} />
              <div className="font-heading text-2xl font-bold" style={{ color: "var(--text)" }}>{s[k]}</div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                {k === "real" ? L({ de: "Echt", fr: "Réel", it: "Reale", en: "Real" }) : (k === "seed" ? "Seed" : "Demo")}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Backup + purge */}
      <Card className="p-5">
        <div className="text-sm font-semibold mb-1" style={{ color: "var(--text)" }}>
          {L({ de: "1. Backup & Bereinigung", fr: "1. Sauvegarde & nettoyage", it: "1. Backup & pulizia", en: "1. Backup & cleanup" })}
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          {L({ de: "Immer zuerst ein Backup herunterladen, dann Seed/Demo archivieren.", fr: "Sauvegardez d'abord, puis archivez.", it: "Prima il backup, poi archivia.", en: "Always download a backup first, then archive seed/demo." })}
        </p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="migration-backup-btn" onClick={backup} disabled={busy}
            className="inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium disabled:opacity-50" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
            <Download className="h-4 w-4" /> {L({ de: "Backup herunterladen", fr: "Télécharger la sauvegarde", it: "Scarica backup", en: "Download backup" })}
          </button>
          <button data-testid="migration-purge-btn" onClick={purge} disabled={busy || (s.seed + s.demo) === 0}
            className="inline-flex h-10 items-center gap-2 rounded-lg border px-4 text-sm font-medium disabled:opacity-50" style={{ borderColor: "rgba(239,68,68,0.4)", color: "#B91C1C" }}>
            <Trash2 className="h-4 w-4" /> {L({ de: "Seed/Demo archivieren", fr: "Archiver seed/démo", it: "Archivia seed/demo", en: "Archive seed/demo" })} ({s.seed + s.demo})
          </button>
        </div>
      </Card>

      {/* CSV import */}
      <Card className="p-5">
        <div className="text-sm font-semibold mb-1" style={{ color: "var(--text)" }}>
          {L({ de: "2. Echte Anbieter importieren (CSV)", fr: "2. Importer des fournisseurs (CSV)", it: "2. Importa fornitori (CSV)", en: "2. Import real providers (CSV)" })}
        </div>
        <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
          {L({ de: "CSV mit Kopfzeile einfügen. Spalten:", fr: "Collez un CSV avec en-tête. Colonnes :", it: "Incolla CSV con intestazione. Colonne:", en: "Paste CSV with a header row. Columns:" })}
          <code className="ml-1 rounded px-1" style={{ background: "var(--surface-alt)" }}>{CSV_HINT}</code>
        </p>
        <textarea data-testid="migration-csv-input" value={csv} onChange={(e) => setCsv(e.target.value)} rows={5}
          placeholder={CSV_HINT + "\nSalon Beispiel,Coiffure,Zürich,info@salon.ch,+41...,https://salon.ch,Kurzbeschreibung"}
          className="w-full rounded-lg border p-3 text-xs font-mono" style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--text)" }} />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-xs" style={{ color: "var(--text-muted)" }}>{L({ de: "Status", fr: "Statut", it: "Stato", en: "Status" })}:</label>
          <select data-testid="migration-status-select" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-lg border px-2 text-sm" style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--text)" }}>
            <option value="pending">{L({ de: "Ausstehend (Freigabe nötig)", fr: "En attente", it: "In attesa", en: "Pending (needs approval)" })}</option>
            <option value="approved">{L({ de: "Direkt freigegeben", fr: "Approuvé", it: "Approvato", en: "Approved" })}</option>
          </select>
          <button data-testid="migration-preview-btn" onClick={() => runImport(false)} disabled={busy || !csv.trim()}
            className="inline-flex h-9 items-center gap-2 rounded-lg border px-4 text-sm font-medium disabled:opacity-50" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
            {L({ de: "Vorschau", fr: "Aperçu", it: "Anteprima", en: "Preview" })}
          </button>
          {preview && preview.valid > 0 && (
            <button data-testid="migration-import-confirm-btn" onClick={() => runImport(true)} disabled={busy}
              className="inline-flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-medium disabled:opacity-50" style={{ background: "var(--accent)", color: "#fff" }}>
              <Upload className="h-4 w-4" /> {L({ de: `Import bestätigen (${preview.valid})`, fr: `Confirmer (${preview.valid})`, it: `Conferma (${preview.valid})`, en: `Confirm import (${preview.valid})` })}
            </button>
          )}
        </div>

        {preview && (
          <div className="mt-4 rounded-lg border" data-testid="migration-preview" style={{ borderColor: "var(--border)" }}>
            <div className="px-3 py-2 text-xs font-semibold" style={{ background: "var(--surface-alt)", color: "var(--text)" }}>
              {L({ de: "Vorschau", fr: "Aperçu", it: "Anteprima", en: "Preview" })}: {preview.valid} ✓ · {preview.invalid} ✕
            </div>
            <div className="max-h-60 overflow-auto divide-y" style={{ borderColor: "var(--border)" }}>
              {preview.rows.map((r) => (
                <div key={r.index} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
                  {r.ok ? <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "#15803D" }} /> : <AlertTriangle className="h-3.5 w-3.5" style={{ color: "#B45309" }} />}
                  <span className="font-medium" style={{ color: "var(--text)" }}>{r.business_name || "—"}</span>
                  {r.location && <span style={{ color: "var(--text-muted)" }}>· {L(r.location)}</span>}
                  {r.categories?.length > 0 && <span style={{ color: "var(--text-muted)" }}>· {r.categories.join(", ")}</span>}
                  {r.warnings?.map((w, i) => (
                    <span key={i} className="rounded-full px-2 py-0.5" style={{ background: "rgba(245,158,11,0.12)", color: "#B45309" }}>{w}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Provider list with origin marking */}
      <Card className="p-5">
        <div className="text-sm font-semibold mb-3" style={{ color: "var(--text)" }}>
          {L({ de: "3. Anbieter markieren (echt / Demo)", fr: "3. Marquer (réel / démo)", it: "3. Marca (reale / demo)", en: "3. Mark providers (real / demo)" })}
        </div>
        <div className="space-y-2 max-h-96 overflow-auto">
          {data.providers.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{L({ de: "Keine Anbieter", fr: "Aucun", it: "Nessuno", en: "None" })}</div>}
          {data.providers.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-2.5" data-testid={`migration-provider-${p.id}`} style={{ borderColor: "var(--border)" }}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{p.business_name}</div>
                <div className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{p.contact_email || "—"} · {p.status}</div>
              </div>
              <StatusMarker kind={ORIGIN_MARK[p.origin] || "synthetic"} testid={`migration-origin-${p.id}`} />
              {p.origin === "demo" ? (
                <button data-testid={`migration-mark-real-${p.id}`} onClick={() => setOrigin(p.id, { is_demo: false, source: "real" })} disabled={busy}
                  className="rounded-lg border px-2.5 py-1 text-[11px] font-medium disabled:opacity-50" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                  {L({ de: "als echt", fr: "réel", it: "reale", en: "as real" })}
                </button>
              ) : (
                <button data-testid={`migration-mark-demo-${p.id}`} onClick={() => setOrigin(p.id, { is_demo: true })} disabled={busy}
                  className="rounded-lg border px-2.5 py-1 text-[11px] font-medium disabled:opacity-50" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                  {L({ de: "als Demo", fr: "démo", it: "demo", en: "as demo" })}
                </button>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Import history + batch rollback */}
      <Card className="p-5">
        <div className="text-sm font-semibold mb-1" style={{ color: "var(--text)" }}>
          {L({ de: "4. Import-Verlauf & Rückgängig", fr: "4. Historique & annulation", it: "4. Storico & annulla", en: "4. Import history & rollback" })}
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          {L({
            de: "Jeder CSV-Import erhält eine eindeutige Batch-ID. Ein Import lässt sich gezielt rückgängig machen — es werden nur dessen Anbieter archiviert, Seed/Demo und andere Daten bleiben unberührt.",
            fr: "Chaque import CSV reçoit un ID de lot unique et peut être annulé de façon ciblée.",
            it: "Ogni import CSV riceve un ID batch univoco e può essere annullato in modo mirato.",
            en: "Each CSV import gets a unique batch ID and can be rolled back precisely — only its providers are archived.",
          })}
        </p>
        {batches.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="import-batches-empty">
            {L({ de: "Noch keine Importe.", fr: "Aucun import.", it: "Nessun import.", en: "No imports yet." })}
          </div>
        ) : (
          <div className="space-y-2" data-testid="import-batches-list">
            {batches.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-2.5" data-testid={`import-batch-${b.id}`} style={{ borderColor: "var(--border)" }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: "var(--text)" }}>
                    {b.count} {L({ de: "Anbieter", fr: "fournisseurs", it: "fornitori", en: "providers" })} · {b.created_at?.slice(0, 10)} · {b.default_status}
                  </div>
                  <div className="text-[11px] font-mono truncate" style={{ color: "var(--text-muted)" }}>
                    #{b.id.slice(0, 8)} · {L({ de: "aktiv", fr: "actifs", it: "attivi", en: "active" })}: {b.remaining_active}
                  </div>
                </div>
                {b.status === "rolled_back" ? (
                  <span className="rounded-full px-2 py-0.5 text-[11px]" data-testid={`import-batch-status-${b.id}`} style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>
                    {L({ de: "rückgängig gemacht", fr: "annulé", it: "annullato", en: "rolled back" })} ({b.rolled_back_count})
                  </span>
                ) : (
                  <button data-testid={`import-batch-rollback-${b.id}`} onClick={() => rollbackBatch(b)} disabled={busy || b.remaining_active === 0}
                    className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-medium disabled:opacity-50" style={{ borderColor: "rgba(239,68,68,0.4)", color: "#B91C1C" }}>
                    <Undo2 className="h-3.5 w-3.5" /> {L({ de: "rückgängig", fr: "annuler", it: "annulla", en: "roll back" })}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
